import { useIsAssumingRole } from "@/hooks/useIsAssumingRole";
import { getSavedHref } from "@/utils/assume-role";
import { useSupplierId, useSupporterId } from "@/utils/roles";
import { config } from "@common/utils/config";
import { Box, Center, Flex, Span, Text } from "@givenwell/components";
import { LogoutIcon, QuestionIcon } from "@givenwell/core-solid-icons";
import { CSS, styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { createLink, CreateLinkProps, Link, linkOptions, useNavigate } from "@tanstack/react-router";
import { NavigationMenu as BaseNavigationMenu } from "radix-ui";
import { ComponentPropsWithoutRef, ForwardedRef, forwardRef, ReactNode } from "react";
import { NewGivenwellLogoMark } from "../GivenwellLogo";
import { EnvironmentBadge } from "../auth/EnvironmentBadge";
import { FlexLink } from "../ui/Link";
import { AccountMenu } from "./AccountMenu";

/**
 * Distance between the left side of the display and the center of icons within a navigation item.
 */
const ICON_CENTER_LINE = 32;
/**
 * approximate width of icons, for aligning section headings with icons
 */
const ICON_SIZE = 22;
/**
 * horizontal gap between item icons and item labels
 */
const ICON_GAP = 8;

const SIDEBAR_PADDING = ICON_CENTER_LINE - ICON_SIZE / 2 - ICON_GAP;
const SECTION_HEADING_PADDING = ICON_GAP;

export function NavigationMenu({
  children,
  header,
  footer,
}: {
  children?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Root orientation="vertical">
      {header && <Box css={{ px: SIDEBAR_PADDING }}>{header}</Box>}
      <Flex css={{ flex: "1 0 0px", flexDirection: "column", overflowY: "auto", px: SIDEBAR_PADDING }}>
        <List>{children}</List>
      </Flex>
      {footer && <Box css={{ px: SIDEBAR_PADDING, pt: SIDEBAR_PADDING }}>{footer}</Box>}
    </Root>
  );
}

export function NavigationMenuHeader() {
  const supplierId = useSupplierId();
  const supporterId = useSupporterId();

  const homeLinkOptions = supplierId
    ? linkOptions({
        to: "/supplier/$supplierId",
        params: { supplierId },
      })
    : supporterId
      ? linkOptions({
          to: "/supporter/$supporterId",
          params: { supporterId },
        })
      : linkOptions({ to: "/admin" });
  return (
    <Flex
      css={{
        height: 72,
        minHeight: 72,
        items: "center",
        pb: 8,
        pos: "relative",
        mt: "max(var(--safe-area-inset-top), 8px)",
      }}
    >
      <Link {...homeLinkOptions}>
        <NewGivenwellLogoMark
          css={{
            height: 32,
            transform: "translateX(5px)",
          }}
        />
      </Link>
      <EnvironmentBadge
        environmentName={config.environmentName}
        isDev={import.meta.env.DEV}
        position={{ top: 10, right: 50 }}
      />
    </Flex>
  );
}

const Root = styled(BaseNavigationMenu.Root, {
  width: "100%",
  height: "100%",

  display: "flex",
  flexDirection: "column",
});
const List = styled(BaseNavigationMenu.List, {
  display: "flex",
  flexDirection: "column",
  height: "100%",
});

type NavigationMenuLinkProps = ComponentPropsWithoutRef<"a"> & {
  css?: CSS;
  icon?: ReactNode;
  activeIcon?: ReactNode;
  badge?: boolean | number;
};

export const NavigationMenuLink = createLink(
  forwardRef(function NavigationMenuLink(
    { css, children, icon, activeIcon, badge: badge, ...props }: CreateLinkProps & NavigationMenuLinkProps,
    ref: ForwardedRef<HTMLAnchorElement>,
  ) {
    const badgeNumberString = typeof badge !== "number" ? "" : badge < 1 ? "" : badge > 9 ? "9+" : badge + "";
    return (
      <Item css={css}>
        <BaseNavigationMenu.Link asChild>
          <StyledA {...(props as any)} ref={ref}>
            {icon && (
              <IconBox className="icon">
                <ActiveIcon>{activeIcon || icon}</ActiveIcon>
                <InactiveIcon>{icon}</InactiveIcon>
                {badge === true && <RedDotBadge />}
              </IconBox>
            )}
            <Span css={{ flex: "1 0 0px" }}>{children}</Span>
            {badgeNumberString && <NumberBadge>{badgeNumberString}</NumberBadge>}
          </StyledA>
        </BaseNavigationMenu.Link>
      </Item>
    );
  }),
);

function RedDotBadge() {
  return (
    <Box
      css={{
        position: "absolute",
        top: 0,
        right: 0,
        rounded: 10,
        bg: colors.red500,
        boxShadow: "0 0 0 2px #fff",
        minW: 10,
        h: 10,
      }}
    />
  );
}

const NumberBadge = styled("div", {
  height: 20,
  minWidth: 20,
  px: 5,
  backgroundColor: colors.pink,
  borderRadius: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontScale: "xs",
  fontFamily: fonts.ftHabit,
  color: colors.text,
  // ".active &": {
  //   opacity: 0,
  // },
});

type NavigationMenuAProps = {
  css?: CSS;
  icon?: ReactNode;
  children?: ReactNode;
  href: string;
};

export function NavigationMenuA({ css, children, icon, ...props }: NavigationMenuAProps) {
  return (
    <Item css={css}>
      <BaseNavigationMenu.Link asChild>
        <StyledA {...props}>
          {icon && <IconBox>{icon}</IconBox>}
          {children}
        </StyledA>
      </BaseNavigationMenu.Link>
    </Item>
  );
}

const Item = styled(BaseNavigationMenu.Item, {
  listStyle: "none",
});

const linkCSS: CSS = {
  height: 34,
  display: "flex",
  alignItems: "center",
  px: ICON_GAP,
  gap: ICON_GAP,

  color: colors.text2,
  fontScale: "sm",
  weight: 500,
  rounded: 9,
  bg: colors.cyanZero,

  "&:hover": {
    backgroundColor: colors.warmGrey,
  },

  "&.active": {
    weight: 500,
    // bg: colors.warmGrey,
    bg: "rgba(0,0,0,0.08)",
    color: colors.navy,
  },
  mb: 2,
};

const StyledA = styled("a", linkCSS);

const IconBox = styled("span", {
  display: "flex",
  items: "center",
  justify: "center",
  position: "relative",
  size: ICON_SIZE,
  flexShrink: 0,

  color: colors.text2,
  ".active > &": {
    color: colors.blue800,
  },
});

const ActiveIcon = styled("span", {
  display: "none",
  "[aria-current=page] &": {
    display: "block",
  },
});
const InactiveIcon = styled("span", {
  "[aria-current=page] &": {
    display: "none",
  },
});

export function NavigationMenuHeading({ children }: { children?: ReactNode }) {
  return (
    <Item>
      <Text
        css={{
          color: colors.text2,
          letterSpacing: "0.2px",
          fontScale: "xs",
          weight: 600,
          // textTransform: "uppercase",
          display: "flex",
          height: 64,
          pt: 28,
          // mt: 8,
          px: SECTION_HEADING_PADDING,
          items: "center",
        }}
      >
        {children}
      </Text>
    </Item>
  );
}

const Hr = styled("hr", { height: 1, backgroundColor: "rgba(0, 0, 0, 0.2)", mx: 8, mt: 16, mb: 8 });

export function SupplierNavigationMenuItems() {
  const isAssuming = useIsAssumingRole("supplier");
  return (
    <>
      <Box css={{ mt: "auto" }} />

      {isAssuming && <AssumeLink />}
      <NavigationMenuA href="mailto:support@givenwell.com?subject=Supplier%20Support%20Request" icon={<QuestionIcon />}>
        Help & Support
      </NavigationMenuA>
      <AccountMenu role="supplier" />
    </>
  );
}

export function SupporterNavigationMenuItems() {
  const isAssuming = useIsAssumingRole("supporter");

  return (
    <>
      {isAssuming && <AssumeLink />}
      <NavigationMenuA href="mailto:support@givenwell.com?subject=Employer%20Support%20Request" icon={<QuestionIcon />}>
        Help & Support
      </NavigationMenuA>
      <AccountMenu role="supporter" />
    </>
  );
}

function AssumeLink() {
  const navigate = useNavigate();
  return (
    <FlexLink
      to="/admin"
      css={{
        padding: 8,
        borderRadius: 12,
        gap: 8,
        backgroundColor: colors.pink,
        color: colors.navy,
        alignItems: "center",
        fontWeight: 500,
        fontSize: 14,
        flexShrink: 100,
      }}
      onClick={e => {
        const savedHref = getSavedHref();
        if (savedHref) {
          e.preventDefault();
          navigate({ href: savedHref });
        }
      }}
    >
      <Center css={{ size: 24, flexShrink: 0 }}>
        <LogoutIcon color={colors.navy} style={{ flexShrink: 0 }} strokeWidth={1.5} />
      </Center>
      <Span css={{ lineClamp: 1 }}>Stop assuming role</Span>
    </FlexLink>
  );
}
